import { LinkListItem } from '../components/layout/LinkList'

/**
 * CJI's existing dimension list, kept word for word for review.
 * Not mapped to NCJI's pillars: reconcile before publication.
 */
export const cjiMeasures: string[] = [
  'Reasoning quality',
  'Consequential thinking',
  'Manipulation resistance',
  'Emotional regulation',
  'Social cooperation',
  'Leadership responsibility',
]

export const cjiUseCases: LinkListItem[] = [
  { title: 'Education', description: 'Support reasoning, media literacy, and judgment development', href: '/education' },
  { title: 'Employers & Organizations', description: 'Strengthen decision quality, communication, and advancement readiness', href: '/employers' },
  { title: 'Civil Society', description: 'Support misinformation resilience and collective reasoning capacity', href: '/civil-society' },
  { title: 'Government', description: 'Structured framework for understanding readiness and judgment quality', href: '/government' },
  { title: 'Leadership', description: 'High-responsibility decision-making and leadership readiness', href: '/leadership' },
]

export const ncjiLifecycle = [
  { stage: 'Early Development', focus: 'Emotional regulation, cooperation, self-direction, and foundational reasoning' },
  { stage: 'Education', focus: 'Logic, media literacy, planning, and consequential thinking' },
  { stage: 'Adulthood', focus: 'Readiness and opportunity-alignment for workforce, entrepreneurship, civic participation, and public service' },
  { stage: 'Institutional & Executive', focus: 'Leadership development, civil-service modernization, and high-responsibility decision environments' },
]

export const ncjiScales: LinkListItem[] = [
  { title: 'Individual Level', description: 'Human development, self-awareness, and opportunity guidance', href: '/individual-level' },
  { title: 'Organizational Level', description: 'Readiness and capability-building inside institutions', href: '/organizational-level' },
  { title: 'Municipal Scale', description: 'Local civic resilience and community strengthening', href: '/municipal-scale' },
  { title: 'National Scale', description: 'Strategic planning for civic resilience, institutional strengthening, and leadership qualification', href: '/national-scale' },
]

export const partnershipTypes: { title: string; description: string; planned?: boolean }[] = [
  {
    title: 'Connected Research & Organizational Knowledge',
    description: 'Work with us to connect papers, reports, policies, and expertise in the Semantic Knowledge Platform, with governed vocabularies, source links, and expert review.',
  },
  {
    title: 'Noetivra Interface & Module Development',
    description: 'Help shape Noetivra’s interactive views and planned modules for research, talent, clinical review, collaboration, and human capability development.',
    planned: true,
  },
  {
    title: 'Domain Expertise & Assessment Validation',
    description: 'Contribute subject-matter expertise, review knowledge models, and help test whether assessment methods are fit for their intended uses.',
  },
  {
    title: 'Integration with Existing Systems',
    description: 'Connect SKP and Noetivra with content management, learning, HR, research, and case systems under appropriate permission controls.',
    planned: true,
  },
  {
    title: 'Knowledge Engineering & Implementation',
    description: 'Partner on ontology design, vocabulary configuration, workflow setup, deployment, and ongoing knowledge stewardship.',
  },
  {
    title: 'Educational Institutions',
    description: 'Partner with universities, schools, and learning systems to integrate judgment development, reasoning quality, and civic capability into curricula and student development programs.',
  },
  {
    title: 'Employers & Organizations',
    description: 'Collaborate with companies and workforce programs to strengthen decision quality, leadership pipelines, and advancement readiness across teams and talent development initiatives.',
  },
  {
    title: 'Civil Society Organizations',
    description: 'Work with nonprofits, community groups, and mentorship networks to build misinformation resilience, opportunity pathways, and local leadership capacity.',
  },
  {
    title: 'Government & Public Institutions',
    description: 'Support municipal, regional, and national governments in workforce modernization, civic resilience planning, civil service development, and leadership qualification frameworks.',
  },
  {
    title: 'Research Institutions',
    description: 'Engage with academic researchers, think tanks, and policy institutes to advance the science of judgment, decision-quality, manipulation resistance, and institutional readiness.',
  },
  {
    title: 'Technology & Delivery Partners',
    description: 'Work with us on complementary tools, assessment services, and delivery capacity to extend reach and improve customer service.',
  },
]

export const partnerOfferings = [
  {
    title: 'Knowledge Infrastructure',
    description: 'SKP implementations adapted to your terminology, sources, permissions, and review processes.',
  },
  {
    title: 'Frameworks & Pilot Access',
    description: 'Access to the CJI and NCJI frameworks for research and pilots, and early participation in planned Noetivra pilots.',
  },
  {
    title: 'Research Collaboration',
    description: 'Joint research initiatives, pilot program support, validation studies, and co-development of frameworks and methodologies.',
  },
  {
    title: 'Implementation Support',
    description: 'Training, onboarding assistance, best practices guidance, and ongoing partnership management.',
  },
  {
    title: 'Strategic Alignment',
    description: 'Co-marketing opportunities, thought leadership collaboration, and alignment on long-term vision and impact goals.',
  },
]

export const partnershipPrinciples = [
  {
    title: 'Shared Mission',
    description: 'We partner with organizations committed to strengthening human capability, improving judgment quality, and creating meaningful pathways for development and opportunity.',
  },
  {
    title: 'Long-Term Orientation',
    description: 'We’re focused on partnerships that create lasting value, not short-term transactions. We invest in relationships that can grow and deepen over time.',
  },
  {
    title: 'Mutual Learning',
    description: 'The best partnerships are bidirectional. We learn from our partners’ expertise, context, and feedback to continuously improve our systems, frameworks, and approach.',
  },
  {
    title: 'Real-World Impact',
    description: 'We prioritize partnerships that translate into measurable improvements in reasoning quality, opportunity access, institutional readiness, and leadership development.',
  },
]
